// ============================================================================
// Identificadores: serie normalizada, RFC, UUID del CFDI e IDs deterministas
// ============================================================================
// El candado de unicidad (`asset_identifiers`) se arma con la serie
// normalizada, así que "pf-3x yz" y "PF3XYZ" son la misma serie. La misma
// normalización se aplica a la serie y al IMEI de `devices` para responder
// `ES_DISPOSITIVO` (D1).
//
// Los IDs deterministas hacen que repetir una operación no cree nada nuevo: se
// escribe con `create()` y el segundo intento choca con el documento existente.
// SHA-1 en TypeScript puro: el paquete no depende de `node:crypto` para usarse
// igual en la pantalla (navegador), en Functions y en scripts.
// ============================================================================

/** Largo máximo de una serie normalizada (cabe de sobra en un id de Firestore). */
export const MAX_SERIAL_LENGTH = 100;

/**
 * Serie normalizada: sin acentos, en mayúsculas y solo letras y dígitos. Vacía
 * si no había nada. Nunca lleva `/`, así que sirve dentro de un id de Firestore.
 */
export function normalizeSerial(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/** RFC sin espacios ni guiones y en mayúsculas. */
export function normalizeRfc(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s\-.]/g, "");
}

// Persona moral (12) o física (13): letras, fecha AAMMDD y homoclave.
const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

/** RFC con forma válida (no consulta al SAT). Espera el valor ya normalizado. */
export function isValidRfc(rfc: string): boolean {
  return RFC_RE.test(rfc);
}

/** UUID del CFDI (folio fiscal) en mayúsculas y sin espacios. */
export function normalizeInvoiceUuid(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** UUID con la forma 8-4-4-4-12. Espera el valor ya normalizado. */
export function isValidInvoiceUuid(uuid: string): boolean {
  return UUID_RE.test(uuid);
}

// --- SHA-1 --------------------------------------------------------------------

function utf8Bytes(s: string): number[] {
  const out: number[] = [];
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
  }
  return out;
}

/** SHA-1 hexadecimal (40 caracteres) de un texto UTF-8. */
export function sha1Hex(input: string): string {
  const bytes = utf8Bytes(input);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const hi = Math.floor(bitLen / 0x100000000);
  const lo = bitLen >>> 0;
  for (const word of [hi, lo]) bytes.push((word >>> 24) & 0xff, (word >>> 16) & 0xff, (word >>> 8) & 0xff, word & 0xff);

  let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe, h3 = 0x10325476, h4 = 0xc3d2e1f0;
  const w = new Array<number>(80);
  const rotl = (x: number, n: number) => (x << n) | (x >>> (32 - n));

  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4;
      w[i] = (bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3];
    }
    for (let i = 16; i < 80; i++) w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    let a = h0, b = h1, c = h2, d = h3, e = h4;
    for (let i = 0; i < 80; i++) {
      let f: number, k: number;
      if (i < 20) { f = (b & c) | (~b & d); k = 0x5a827999; }
      else if (i < 40) { f = b ^ c ^ d; k = 0x6ed9eba1; }
      else if (i < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8f1bbcdc; }
      else { f = b ^ c ^ d; k = 0xca62c1d6; }
      const t = (rotl(a, 5) + f + e + k + w[i]) | 0;
      e = d; d = c; c = rotl(b, 30); b = a; a = t;
    }
    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0; h4 = (h4 + e) | 0;
  }
  return [h0, h1, h2, h3, h4].map((h) => (h >>> 0).toString(16).padStart(8, "0")).join("");
}

// --- IDs deterministas ---------------------------------------------------------

/** Candado de la serie: `asset_identifiers/{companyId}_{identifierNorm}`. */
export function identifierLockId(companyId: string, identifierNorm: string): string {
  return `${companyId}_${normalizeSerial(identifierNorm)}`;
}

/** Compra con UUID: `pur_{sha1(company|UUID)}`. La misma factura no se registra dos veces. */
export function purchaseIdForInvoice(companyId: string, invoiceUuid: string): string {
  return "pur_" + sha1Hex(`${companyId}|${normalizeInvoiceUuid(invoiceUuid)}`);
}

/** Proveedor con RFC: `sup_{sha1(company|RFC)}`. */
export function supplierIdForRfc(companyId: string, rfc: string): string {
  return "sup_" + sha1Hex(`${companyId}|${normalizeRfc(rfc)}`);
}

/** Gasto que crea Activos para una compra (PR 4): `exp_asset_{purchaseId}`. */
export function purchaseExpenseId(purchaseId: string): string {
  return `exp_asset_${purchaseId}`;
}

/** Gasto que crea Activos para un mantenimiento (PR 5): `exp_assetmnt_{id}`. */
export function maintenanceExpenseId(maintenanceId: string): string {
  return `exp_assetmnt_${maintenanceId}`;
}
